import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import CelsiusLogo from "@/components/CelsiusLogo";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("credentials"); // credentials | otp
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password, step === "otp" ? otp : undefined);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // 2FA challenge: server sends {code: "otp_required"} — switch to OTP step
      if (detail && typeof detail === "object" && detail.code === "otp_required") {
        setStep("otp");
        setErr("");
      } else if (detail && typeof detail === "object" && detail.code === "otp_invalid") {
        setErr("The 6-digit code was wrong. Try again with the current code from your authenticator app.");
      } else {
        setErr(formatApiErrorDetail(detail) || e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      {/* Left – form */}
      <div className="flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-sm">
          <div className="mb-12">
            <CelsiusLogo variant="full" scheme="light" size={44} />
          </div>

          <div className="label-caps mb-3">{step === "otp" ? "Two-factor" : "Sign In"}</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-2">
            {step === "otp" ? "One more step." : "Command your operation."}
          </h1>
          <p className="text-sm text-slate-500 mb-8">
            {step === "otp"
              ? "Enter the 6-digit code from your authenticator app."
              : "Projects · Invoices · Payments · Expenses · Profit"}
          </p>

          <form onSubmit={submit} className="space-y-5" autoComplete="off">
            {step === "credentials" && (
              <>
                <div>
                  <Label htmlFor="email" className="label-caps">Email</Label>
                  <Input
                    id="email" name="email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                    autoComplete="username"
                    placeholder="you@company.com"
                    className="rounded-sm mt-2 h-11 border-slate-300"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="label-caps">Password</Label>
                  <Input
                    id="password" name="password" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="rounded-sm mt-2 h-11 border-slate-300"
                    required
                  />
                </div>
              </>
            )}

            {step === "otp" && (
              <div className="border border-slate-200 rounded-sm p-4 bg-slate-50">
                <div className="flex items-center gap-2 text-slate-700 text-sm mb-3">
                  <ShieldCheck size={16} weight="fill" className="text-emerald-600" />
                  Signed in as <strong>{email}</strong>
                </div>
                <Label htmlFor="otp" className="label-caps">6-digit code</Label>
                <Input
                  id="otp" value={otp} inputMode="numeric" autoFocus
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  data-testid="login-otp-input"
                  className="rounded-sm mt-2 h-11 border-slate-300 font-mono-num tracking-widest text-lg text-center"
                  placeholder="123456" maxLength={6} required
                />
                <button
                  type="button"
                  onClick={() => { setStep("credentials"); setOtp(""); setErr(""); }}
                  className="mt-3 text-[11px] uppercase tracking-widest text-slate-500 hover:text-[#EA580C]"
                  data-testid="cancel-otp"
                >
                  ← Use different account
                </button>
              </div>
            )}

            {err && (
              <div data-testid="login-error" className="text-sm border border-red-200 bg-red-50 text-red-700 px-3 py-2 rounded-sm">
                {err}
              </div>
            )}

            <Button
              type="submit" disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white font-medium"
            >
              {loading ? "Signing in..." : step === "otp" ? "Verify & continue" : "Sign in"}
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </form>

          {step === "credentials" && (
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="label-caps mb-2">Need help?</div>
              <div className="text-xs text-slate-500">
                Contact your Celsius administrator to request access.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right – image */}
      <div className="hidden lg:block relative bg-slate-900">
        <img
          src="https://images.pexels.com/photos/27427771/pexels-photo-27427771.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="HVAC unit"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/70 via-slate-900/20 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="label-caps text-white/70 mb-3">Enterprise Grade</div>
          <h2 className="font-display text-3xl font-bold max-w-md leading-tight">
            Every duct, dollar and deadline — one operating system.
          </h2>
          <p className="text-sm text-white/70 max-w-md mt-3">
            Built for large-scale HVAC contractors who ship complex projects on time and profitably.
          </p>
        </div>
      </div>
    </div>
  );
}
